package fr.frux.core.database

import app.cash.sqldelight.Query
import app.cash.sqldelight.TransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.Double
import kotlin.Long
import kotlin.String

public class TreeQueries(
  driver: SqlDriver,
) : TransacterImpl(driver) {
  public fun <T : Any> selectAll(mapper: (
    id: Long,
    species_id: Long,
    species_common_name_fr: String,
    species_scientific_name: String,
    species_form: String,
    harvest_part: String?,
    latitude: Double,
    longitude: Double,
    status: String,
    certainty: String,
    disputed: Long,
    description: String?,
    photo_url: String?,
    last_observed_at: Long?,
    created_by: Long,
    created_at: Long,
    updated_at: Long,
    cached_at: Long,
    site_id: Long?,
  ) -> T): Query<T> = Query(911_317_772, arrayOf("tree"), driver, "tree.sq", "selectAll", "SELECT tree.id, tree.species_id, tree.species_common_name_fr, tree.species_scientific_name, tree.species_form, tree.harvest_part, tree.latitude, tree.longitude, tree.status, tree.certainty, tree.disputed, tree.description, tree.photo_url, tree.last_observed_at, tree.created_by, tree.created_at, tree.updated_at, tree.cached_at, tree.site_id FROM tree") { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getLong(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5),
      cursor.getDouble(6)!!,
      cursor.getDouble(7)!!,
      cursor.getString(8)!!,
      cursor.getString(9)!!,
      cursor.getLong(10)!!,
      cursor.getString(11),
      cursor.getString(12),
      cursor.getLong(13),
      cursor.getLong(14)!!,
      cursor.getLong(15)!!,
      cursor.getLong(16)!!,
      cursor.getLong(17)!!,
      cursor.getLong(18)
    )
  }

  public fun selectAll(): Query<Tree> = selectAll(::Tree)

  public fun <T : Any> selectById(id: Long, mapper: (
    id: Long,
    species_id: Long,
    species_common_name_fr: String,
    species_scientific_name: String,
    species_form: String,
    harvest_part: String?,
    latitude: Double,
    longitude: Double,
    status: String,
    certainty: String,
    disputed: Long,
    description: String?,
    photo_url: String?,
    last_observed_at: Long?,
    created_by: Long,
    created_at: Long,
    updated_at: Long,
    cached_at: Long,
    site_id: Long?,
  ) -> T): Query<T> = SelectByIdQuery(id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getLong(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5),
      cursor.getDouble(6)!!,
      cursor.getDouble(7)!!,
      cursor.getString(8)!!,
      cursor.getString(9)!!,
      cursor.getLong(10)!!,
      cursor.getString(11),
      cursor.getString(12),
      cursor.getLong(13),
      cursor.getLong(14)!!,
      cursor.getLong(15)!!,
      cursor.getLong(16)!!,
      cursor.getLong(17)!!,
      cursor.getLong(18)
    )
  }

  public fun selectById(id: Long): Query<Tree> = selectById(id, ::Tree)

  public fun <T : Any> selectInBounds(
    latMin: Double,
    latMax: Double,
    lngMin: Double,
    lngMax: Double,
    minCachedAt: Long,
    mapper: (
      id: Long,
      species_id: Long,
      species_common_name_fr: String,
      species_scientific_name: String,
      species_form: String,
      harvest_part: String?,
      latitude: Double,
      longitude: Double,
      status: String,
      certainty: String,
      disputed: Long,
      description: String?,
      photo_url: String?,
      last_observed_at: Long?,
      created_by: Long,
      created_at: Long,
      updated_at: Long,
      cached_at: Long,
      site_id: Long?,
    ) -> T,
  ): Query<T> = SelectInBoundsQuery(latMin, latMax, lngMin, lngMax, minCachedAt) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getLong(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5),
      cursor.getDouble(6)!!,
      cursor.getDouble(7)!!,
      cursor.getString(8)!!,
      cursor.getString(9)!!,
      cursor.getLong(10)!!,
      cursor.getString(11),
      cursor.getString(12),
      cursor.getLong(13),
      cursor.getLong(14)!!,
      cursor.getLong(15)!!,
      cursor.getLong(16)!!,
      cursor.getLong(17)!!,
      cursor.getLong(18)
    )
  }

  public fun selectInBounds(
    latMin: Double,
    latMax: Double,
    lngMin: Double,
    lngMax: Double,
    minCachedAt: Long,
  ): Query<Tree> = selectInBounds(latMin, latMax, lngMin, lngMax, minCachedAt, ::Tree)

  public fun countAll(): Query<Long> = Query(-1_534_229_205, arrayOf("tree"), driver, "tree.sq", "countAll", "SELECT COUNT(*) FROM tree") { cursor ->
    cursor.getLong(0)!!
  }

  /**
   * @return The number of rows updated.
   */
  public fun upsert(
    id: Long?,
    species_id: Long,
    species_common_name_fr: String,
    species_scientific_name: String,
    species_form: String,
    harvest_part: String?,
    latitude: Double,
    longitude: Double,
    status: String,
    certainty: String,
    disputed: Long,
    description: String?,
    photo_url: String?,
    last_observed_at: Long?,
    created_by: Long,
    created_at: Long,
    updated_at: Long,
    cached_at: Long,
    site_id: Long?,
  ): QueryResult<Long> {
    val result = driver.execute(554_806_920, """
        |INSERT OR REPLACE INTO tree(
        |  id, species_id, species_common_name_fr, species_scientific_name, species_form,
        |  harvest_part, latitude, longitude, status, certainty, disputed, description,
        |  photo_url, last_observed_at, created_by, created_at, updated_at, cached_at, site_id
        |) VALUES (
        |  ?, ?, ?, ?, ?,
        |  ?, ?, ?, ?, ?, ?, ?,
        |  ?, ?, ?, ?, ?, ?, ?
        |)
        """.trimMargin(), 19) {
          var parameterIndex = 0
          bindLong(parameterIndex++, id)
          bindLong(parameterIndex++, species_id)
          bindString(parameterIndex++, species_common_name_fr)
          bindString(parameterIndex++, species_scientific_name)
          bindString(parameterIndex++, species_form)
          bindString(parameterIndex++, harvest_part)
          bindDouble(parameterIndex++, latitude)
          bindDouble(parameterIndex++, longitude)
          bindString(parameterIndex++, status)
          bindString(parameterIndex++, certainty)
          bindLong(parameterIndex++, disputed)
          bindString(parameterIndex++, description)
          bindString(parameterIndex++, photo_url)
          bindLong(parameterIndex++, last_observed_at)
          bindLong(parameterIndex++, created_by)
          bindLong(parameterIndex++, created_at)
          bindLong(parameterIndex++, updated_at)
          bindLong(parameterIndex++, cached_at)
          bindLong(parameterIndex++, site_id)
        }
    notifyQueries(554_806_920) { emit ->
      emit("tree")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteById(id: Long): QueryResult<Long> {
    val result = driver.execute(1_606_666_486, """DELETE FROM tree WHERE id = ?""", 1) {
          var parameterIndex = 0
          bindLong(parameterIndex++, id)
        }
    notifyQueries(1_606_666_486) { emit ->
      emit("tree")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteByCreator(created_by: Long): QueryResult<Long> {
    val result = driver.execute(-815_991_503, """DELETE FROM tree WHERE created_by = ?""", 1) {
          var parameterIndex = 0
          bindLong(parameterIndex++, created_by)
        }
    notifyQueries(-815_991_503) { emit ->
      emit("tree")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteExpired(threshold: Long): QueryResult<Long> {
    val result = driver.execute(-539_068_543, """DELETE FROM tree WHERE cached_at < ? AND id NOT IN (SELECT tree_id FROM favorite)""", 1) {
          var parameterIndex = 0
          bindLong(parameterIndex++, threshold)
        }
    notifyQueries(-539_068_543) { emit ->
      emit("tree")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteOldestOverflow(n: Long): QueryResult<Long> {
    val result = driver.execute(1_461_112_645, """
        |DELETE FROM tree WHERE id IN (
        |  SELECT id FROM tree WHERE id NOT IN (SELECT tree_id FROM favorite) ORDER BY cached_at ASC LIMIT ?
        |)
        """.trimMargin(), 1) {
          var parameterIndex = 0
          bindLong(parameterIndex++, n)
        }
    notifyQueries(1_461_112_645) { emit ->
      emit("tree")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun clearAll(): QueryResult<Long> {
    val result = driver.execute(-371_904_723, """DELETE FROM tree""", 0)
    notifyQueries(-371_904_723) { emit ->
      emit("tree")
    }
    return result
  }

  private inner class SelectByIdQuery<out T : Any>(
    public val id: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("tree", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("tree", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(-1_813_878_841, """SELECT tree.id, tree.species_id, tree.species_common_name_fr, tree.species_scientific_name, tree.species_form, tree.harvest_part, tree.latitude, tree.longitude, tree.status, tree.certainty, tree.disputed, tree.description, tree.photo_url, tree.last_observed_at, tree.created_by, tree.created_at, tree.updated_at, tree.cached_at, tree.site_id FROM tree WHERE id = ?""", mapper, 1) {
      var parameterIndex = 0
      bindLong(parameterIndex++, id)
    }

    override fun toString(): String = "tree.sq:selectById"
  }

  private inner class SelectInBoundsQuery<out T : Any>(
    public val latMin: Double,
    public val latMax: Double,
    public val lngMin: Double,
    public val lngMax: Double,
    public val minCachedAt: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("tree", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("tree", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(258_918_831, """
    |SELECT tree.id, tree.species_id, tree.species_common_name_fr, tree.species_scientific_name, tree.species_form, tree.harvest_part, tree.latitude, tree.longitude, tree.status, tree.certainty, tree.disputed, tree.description, tree.photo_url, tree.last_observed_at, tree.created_by, tree.created_at, tree.updated_at, tree.cached_at, tree.site_id FROM tree
    |WHERE latitude BETWEEN ? AND ?
    |  AND longitude BETWEEN ? AND ?
    |  AND cached_at >= ?
    """.trimMargin(), mapper, 5) {
      var parameterIndex = 0
      bindDouble(parameterIndex++, latMin)
      bindDouble(parameterIndex++, latMax)
      bindDouble(parameterIndex++, lngMin)
      bindDouble(parameterIndex++, lngMax)
      bindLong(parameterIndex++, minCachedAt)
    }

    override fun toString(): String = "tree.sq:selectInBounds"
  }
}
